import {html, css} from 'lit';
import {Service} from '../../service/service.js';
import {BaseComponent} from '../basecomponent.js';

/**
 * Polls the server app version and shows a non-blocking banner when a
 * Blue/Green (or other) cutover has switched the serving version. The user
 * chooses when to refresh so in-progress work is not interrupted.
 * The banner can be minimized to a small chip so toolbars stay usable.
 */
class VersionWatcher extends BaseComponent {

	static get properties() {
		return {
			active: {type: Boolean},
			interval: {type: Number},
			updateAvailable: {type: Boolean},
			collapsed: {type: Boolean},
			loadedVersion: {type: String},
			serverVersion: {type: String}
		};
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}

			.version-update-banner {
				position: fixed;
				top: 64px;
				left: 0;
				right: 0;
				z-index: 10050;
				display: flex;
				align-items: center;
				justify-content: center;
				flex-wrap: wrap;
				gap: 12px;
				padding: 10px 16px;
				box-sizing: border-box;
				background: #1f4e79;
				color: #ffffff;
				border-bottom: 2px solid #163a5a;
				box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
				font-size: 14px;
				line-height: 1.4;
				text-align: center;
			}

			.version-update-banner .message {
				max-width: 52rem;
			}

			.version-update-banner a.refresh-link {
				color: #ffe08a;
				font-weight: 600;
				text-decoration: underline;
				cursor: pointer;
			}

			.version-update-banner a.refresh-link:hover {
				color: #fff3c4;
			}

			.version-update-banner .actions {
				display: flex;
				align-items: center;
				gap: 8px;
				flex-wrap: wrap;
			}

			.version-update-chip {
				position: fixed;
				top: 70px;
				right: 12px;
				left: auto;
				z-index: 10050;
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 8px 12px;
				box-sizing: border-box;
				background: #1f4e79;
				color: #ffffff;
				border: 1px solid #163a5a;
				border-radius: 6px;
				box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
				font-size: 13px;
				line-height: 1.3;
				cursor: pointer;
				max-width: min(320px, calc(100vw - 24px));
			}

			.version-update-chip:hover {
				background: #265f93;
			}

			.version-update-chip .chip-label {
				flex: 1 1 auto;
				min-width: 0;
			}

			.version-update-chip .chip-version {
				color: #ffe08a;
				font-weight: 600;
			}

			@media only screen and (max-width: 600px) {
				.version-update-banner {
					top: 56px;
					font-size: 12px;
					padding: 8px 10px;
					gap: 8px;
				}

				.version-update-chip {
					top: 60px;
					right: 8px;
					font-size: 12px;
					padding: 6px 10px;
				}
			}
		`;
	}

	constructor() {
		super();
		this.active = true;
		this.interval = 60 * 1000;
		this.updateAvailable = false;
		this.collapsed = false;
		this.loadedVersion = '';
		this.serverVersion = '';
		this.watchId = null;

		var versionWatcher = this;
		setTimeout(function() {
			versionWatcher.init();
		});
	}

	render() {
		if (!this.updateAvailable) {
			return html``;
		}

		if (this.collapsed) {
			return html`
				<div class="version-update-chip"
					role="status"
					aria-live="polite"
					title="Click to expand version update details"
					@click=${this.expandBanner}>
					<span class="chip-label">
						New version
						${this.serverVersion ? html`<span class="chip-version">${this.serverVersion}</span>` : html``}
						available
					</span>
					<vaadin-button theme="tertiary-inline contrast" @click=${this.refreshBrowserFromChip}>
						Refresh
					</vaadin-button>
				</div>
			`;
		}

		return html`
			<div class="version-update-banner" role="status" aria-live="polite">
				<div class="message">
					A new application version is available
					${this.serverVersion ? html` (<b>${this.serverVersion}</b>)` : html``}.
					You are still on
					${this.loadedVersion ? html`<b>${this.loadedVersion}</b>` : html`an older version`}.
					Finish what you are doing, then
					<a class="refresh-link" href="#" @click=${this.refreshBrowser}>refresh your browser</a>
					to load the update.
				</div>
				<div class="actions">
					<vaadin-button theme="primary contrast" @click=${this.refreshBrowser}>
						Refresh now
					</vaadin-button>
					<vaadin-button theme="tertiary contrast" @click=${this.collapseBanner}>
						Minimize
					</vaadin-button>
				</div>
			</div>
		`;
	}

	init() {
		var versionWatcher = this;
		versionWatcher.checkVersion = function() {
			Service.getApplicationService().pollApplicationVersion().then(function(version) {
				if (!version) {
					return;
				}
				if (!versionWatcher.loadedVersion) {
					versionWatcher.loadedVersion = version;
					versionWatcher.serverVersion = version;
					return;
				}
				if (version !== versionWatcher.loadedVersion) {
					versionWatcher.serverVersion = version;
					versionWatcher.updateAvailable = true;
				}
			});
		};

		versionWatcher.checkVersion();
		if (versionWatcher.active) {
			versionWatcher.startWatch();
		}
	}

	collapseBanner(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.collapsed = true;
	}

	expandBanner(event) {
		if (event) {
			event.preventDefault();
		}
		this.collapsed = false;
	}

	refreshBrowserFromChip(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.refreshBrowser();
	}

	refreshBrowser(event) {
		if (event) {
			event.preventDefault();
		}
		window.location.reload();
	}

	stopWatch() {
		if (this.watchId) {
			clearInterval(this.watchId);
			this.watchId = null;
		}
	}

	startWatch(newInterval) {
		this.stopWatch();
		newInterval = newInterval || this.interval;
		this.interval = newInterval;
		this.watchId = setInterval(this.checkVersion, this.interval);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.stopWatch();
	}

};

customElements.define('dl-version-watcher', VersionWatcher);
